import type { PositionRequest } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";
import { useDraftEditor } from "@/hooks/use-draft-editor";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Note } from "@/components/ui/Note";
import type { MappableElement } from "./element-mapping";
import { ElementPicker } from "./ElementPicker";

/** What the form starts from: nothing, for a new position; the position, for an amendment. */
export interface PositionDraft {
  title: string;
  element_ids: string[];
  notes: string;
  /** Why an amendment changes the argument; read only when it does. */
  amendment_note?: string;
}

export const EMPTY_DRAFT: PositionDraft = {
  title: "",
  element_ids: [],
  notes: "",
};

/** Whether the draft changes the argument: the name or the elements it rests on. */
function movesArgument(draft: PositionDraft, initial: PositionDraft): boolean {
  return (
    draft.title.trim() !== initial.title.trim() ||
    draft.element_ids.length !== initial.element_ids.length ||
    draft.element_ids.some((id, i) => id !== initial.element_ids[i])
  );
}

/** The request the draft becomes: a blank note is absent rather than empty, and the amendment note goes only with a change to the argument. */
function toRequest(draft: PositionDraft, moved: boolean): PositionRequest {
  const request: PositionRequest = {
    title: draft.title.trim(),
    element_ids: draft.element_ids,
    notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
  };
  return moved
    ? { ...request, amendment_note: (draft.amendment_note ?? "").trim() }
    : request;
}

/** The server's bound on an amendment's note. */
const MAX_AMENDMENT_NOTE = 1000;

/** Why the amendment changes the argument, asked for while it does, and required. */
function AmendmentNote({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const blank = value.trim() === "";
  const errorId = `${idPrefix}-amendment-note-error`;
  return (
    <div>
      <FieldLabel htmlFor={`${idPrefix}-amendment-note`} required>
        Amendment notes
      </FieldLabel>
      <Textarea
        id={`${idPrefix}-amendment-note`}
        rows={2}
        aria-required
        aria-invalid={blank || undefined}
        aria-describedby={blank ? errorId : undefined}
        maxLength={MAX_AMENDMENT_NOTE}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Why did you amend this position, and what informed these specific changes? Note the prior art or element scope that drove them."
        className="bg-card text-[12px]"
      />
      {blank && (
        <Note id={errorId} tone="destructive" className="mt-1">
          Amendment notes must be provided to save this change.
        </Note>
      )}
    </div>
  );
}

/** Submit and cancel, in the order the form reads them. */
function FormActions({
  submitLabel,
  canSubmit,
  onCancel,
}: {
  submitLabel: string;
  canSubmit: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="submit"
        size="dense"
        disabled={!canSubmit}
        className="cursor-pointer"
      >
        {submitLabel}
      </Button>
      <Button
        type="button"
        size="dense"
        variant="quiet"
        onClick={onCancel}
        className="cursor-pointer"
      >
        Cancel
      </Button>
    </div>
  );
}

/** The name, the elements it rests on and the notes: what every position form asks. */
function Fields({
  idPrefix,
  draft,
  elements,
  setDraft,
}: {
  idPrefix: string;
  draft: PositionDraft;
  elements: readonly MappableElement[];
  setDraft: (update: (d: PositionDraft) => PositionDraft) => void;
}) {
  return (
    <>
      <div>
        <FieldLabel htmlFor={`${idPrefix}-title`}>Position name</FieldLabel>
        <Input
          id={`${idPrefix}-title`}
          value={draft.title}
          onChange={(event) =>
            setDraft((d) => ({ ...d, title: event.target.value }))
          }
          placeholder="e.g. Crystal chamber enables field-swappable blades"
          className="bg-card text-[12.5px]"
        />
      </div>
      <div>
        <FieldLabel optional>Rests on invention elements</FieldLabel>
        <ElementPicker
          elements={elements}
          selected={draft.element_ids}
          onChange={(element_ids) => setDraft((d) => ({ ...d, element_ids }))}
        />
      </div>
      <div>
        <FieldLabel htmlFor={`${idPrefix}-notes`} optional>
          Additional notes
        </FieldLabel>
        <Textarea
          id={`${idPrefix}-notes`}
          rows={2}
          value={draft.notes}
          onChange={(event) =>
            setDraft((d) => ({ ...d, notes: event.target.value }))
          }
          placeholder="Anything you want recorded against this position"
          className="bg-card text-[12px]"
        />
      </div>
    </>
  );
}

/**
 * The form for a position, whether new or being amended.
 *
 * The name is mandatory and the submit stays disabled without one. The
 * mapping and the notes are optional and say so. An amendment that changes
 * the argument asks why, and stays closed until it says. Nothing is sent
 * until the attorney submits; cancel discards the draft and keeps nothing.
 */
export function PositionForm({
  idPrefix,
  initial,
  elements,
  submitLabel,
  busy,
  amending = false,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  initial: PositionDraft;
  /** The form amends a position, so a change to its argument asks why. */
  amending?: boolean;
  elements: readonly MappableElement[];
  submitLabel: string;
  busy: boolean;
  onSubmit: (request: PositionRequest) => void;
  onCancel: () => void;
}) {
  const { draft, setDraft } = useDraftEditor<PositionDraft>(initial, {
    dirty: (a, b) =>
      a.title !== b.title ||
      a.notes !== b.notes ||
      a.element_ids.length !== b.element_ids.length ||
      a.element_ids.some((id, i) => id !== b.element_ids[i]),
  });
  const moved = amending && movesArgument(draft, initial);
  const ready =
    draft.title.trim() !== "" &&
    (!moved || (draft.amendment_note ?? "").trim() !== "");
  return (
    <Panel
      as="form"
      shape="row"
      pad="tight"
      data-testid="position-form"
      className="mt-2 flex flex-col gap-3 border-primary/20 bg-primary/[0.04]"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) onSubmit(toRequest(draft, moved));
      }}
    >
      <Fields
        idPrefix={idPrefix}
        draft={draft}
        elements={elements}
        setDraft={setDraft}
      />
      {moved && (
        <AmendmentNote
          idPrefix={idPrefix}
          value={draft.amendment_note ?? ""}
          onChange={(amendment_note) =>
            setDraft((d) => ({ ...d, amendment_note }))
          }
        />
      )}
      <FormActions
        submitLabel={submitLabel}
        canSubmit={ready && !busy}
        onCancel={onCancel}
      />
    </Panel>
  );
}
